import React, { FC } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { Button, Pagination, Text } from 'components/UI';

import Filters, { IFilter } from 'components/Filters';
import PortfolioItem from 'components/PortfolioItem';

import {
  IAllContentfulPortfolioFilters,
  IAllContentfulPortfolioItem,
  IContentfulPortfolioItem,
  IPaginationContext,
} from 'types';

import generateFilterData from 'utils/generateFilterData';

import styles from './portfolio.module.scss';

type IPortfolioFiltersData = {
  stage?: string;
  sector?: string;
};

interface IContentfulPortfolioFilters {
  portfolioStageFilters: IAllContentfulPortfolioFilters,
  portfolioSectorFilters: IAllContentfulPortfolioFilters,
}

export interface IPortfolioProps {
  data: {
    portfolioData: IAllContentfulPortfolioItem;
  };
  pageContext: IPaginationContext & IPortfolioFiltersData;
}

type IProps = IPortfolioProps & {
  title: string;
  isPreview?: boolean;
};

const baseLinkRenderer = ({ stage, sector }: IPortfolioFiltersData, page: number) => {
  const pageLink = page === 1 ? '' : `/${page.toString()}`;
  if (stage && sector) {
    return `/portfolio/${stage.toLowerCase()}--${sector.toLowerCase()}${pageLink}`;
  }
  return ((stage || sector) ? `/portfolio/${(stage || sector)?.toLowerCase()}${pageLink}` : `/portfolio${pageLink}`);
};

const filterLinkRenderer = (fs: IPortfolioFiltersData) => baseLinkRenderer(fs, 1);
const pageLinkRenderer = (fs: IPortfolioFiltersData) => (p: number) => baseLinkRenderer(fs, p);

const Portfolio: FC<IProps> = ({
  title: pageTitle,
  isPreview,
  data,
  pageContext,
}) => {
  const {
    stage,
    sector,
    currentPage,
    totalPages,
  } = pageContext;

  const selectedFilters = { stage, sector };

  const filters: IContentfulPortfolioFilters = useStaticQuery(graphql`
    query {
      portfolioStageFilters: allContentfulPortfolioStage(
        sort: { fields: order, order: ASC }
      ) {
        nodes {
          title
        }
      }
      portfolioSectorFilters: allContentfulPortfolioSector(
        sort: { fields: order, order: ASC }
      ) {
        nodes {
          title
        }
      }
    }
  `);

  const filtersData: IFilter<IPortfolioFiltersData>[] = [
    generateFilterData<IPortfolioFiltersData>('stage', filters.portfolioStageFilters.nodes),
    generateFilterData<IPortfolioFiltersData>('sector', filters.portfolioSectorFilters.nodes),
  ];

  const items = data.portfolioData.nodes
    .map(({
      title,
      text,
      logo: {
        file: {
          url,
        },
      },
    }: IContentfulPortfolioItem) => ({
      title,
      text,
      slug: title || '404',
      image: url,
    }));

  return (
    <>
      <div className={styles.header}>
        <Text category="h2">{pageTitle}</Text>
        <div id="portfolio-filter-toggle" />
      </div>
      <Filters<IPortfolioFiltersData>
        toggleId="portfolio-filter-toggle"
        data={filtersData}
        selection={selectedFilters}
        linkRenderer={filterLinkRenderer}
      />
      <div className={styles.items}>
        {items.map(({
          slug,
          title,
          text,
          image,
        }) => (
          <PortfolioItem
            key={slug}
            title={title}
            image={image}
            text={text}
          />
        ))}
      </div>
      {isPreview && (
        <div className={styles.buttonWrapper}>
          <Button link="/portfolio">View All</Button>
        </div>
      )}
      {!isPreview && (
        <Pagination
          linkRenderer={pageLinkRenderer({ stage, sector })}
          totalPages={totalPages}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default Portfolio;
